/**
 * 숫자를 천 단위 콤마 포맷으로 변환
 * @example formatNumber('1120000') => '1,120,000'
 */
export function formatNumber(value: string): string {
  if (!value) return ''

  const digits = value.replace(/[^\d]/g, '')
  if (!digits) return ''

  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * 포맷된 숫자에서 순수 숫자만 추출
 * @example parseNumber('1,120,000') => '1120000'
 */
export function parseNumber(value: string): string {
  return value.replace(/[^\d]/g, '')
}

/**
 * 전화번호 입력값을 실시간으로 포맷
 * - 숫자만 남기고 하이픈(-)을 단계적으로 추가
 * - 국내: 010(휴대폰), 02(서울), 0xx(지역/070), 1588/1600/1800(대표번호), 050x(안심번호)
 * - 국제(+82): `+8210...` 형태도 허용하고 `+82-10-....`로 표시
 */
export function formatPhone(phone: string): string {
  if (!phone) return ''

  const raw = String(phone).trim()
  const isPlus = raw.startsWith('+')

  // 숫자만 남기고 과도한 길이는 잘라냄 (최대 12자리)
  const digits = raw.replace(/[^\d]/g, '').slice(0, 12)
  if (!digits) return ''

  if (isPlus && digits.startsWith('82')) {
    return formatKoreanInternational(digits)
  }

  return formatKoreanDomestic(digits)
}

/**
 * 국제 전화번호 포맷 (+82)
 */
function formatKoreanInternational(digits: string): string {
  // +82 제거 후 국내 번호로 변환
  const domestic = digits.slice(2)
  if (!domestic) return '+82'

  // 휴대폰 (10으로 시작)
  if (domestic.startsWith('10')) {
    const formatted = formatMobile(`0${domestic}`).slice(1)
    return `+82-${formatted}`
  }

  // 서울 (2로 시작)
  if (domestic.startsWith('2')) {
    const formatted = formatSeoul(`0${domestic}`).slice(1)
    return `+82-${formatted}`
  }

  // 일반 지역번호
  const formatted = formatRegional(`0${domestic}`).slice(1)
  return `+82-${formatted}`
}

/**
 * 국내 전화번호 포맷
 */
function formatKoreanDomestic(digits: string): string {
  // 휴대폰 (010, 011, 016, 017, 018, 019)
  if (/^01[0-9]/.test(digits)) {
    return formatMobile(digits)
  }

  // 서울 (02)
  if (digits.startsWith('02')) {
    return formatSeoul(digits)
  }

  // 대표번호 (1588, 1577, 1600, 1800 등)
  if (/^1[5-8]/.test(digits)) {
    return formatRepresentative(digits)
  }

  // 안심번호 (050x)
  if (digits.startsWith('050')) {
    return formatSafeNumber(digits)
  }

  // 070 인터넷전화
  if (digits.startsWith('070')) {
    return formatInternetPhone(digits)
  }

  // 일반 지역번호 (0xx)
  if (digits.startsWith('0')) {
    return formatRegional(digits)
  }

  return digits
}

/**
 * 휴대폰 포맷 (010-XXXX-XXXX)
 */
function formatMobile(digits: string): string {
  if (digits.length <= 3) return digits
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`
}

/**
 * 서울 지역번호 포맷 (02-XXX-XXXX 또는 02-XXXX-XXXX)
 */
function formatSeoul(digits: string): string {
  if (digits.length <= 2) return digits
  if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`
  if (digits.length <= 9)
    return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`
  return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}`
}

/**
 * 일반 지역번호 포맷 (0xx-XXX-XXXX 또는 0xx-XXXX-XXXX)
 */
function formatRegional(digits: string): string {
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  if (digits.length <= 10)
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`
}

/**
 * 대표번호 포맷 (1588-XXXX)
 */
function formatRepresentative(digits: string): string {
  if (digits.length <= 4) return digits
  return `${digits.slice(0, 4)}-${digits.slice(4, 8)}`
}

/**
 * 안심번호 포맷 (050x-XXX-XXXX 또는 050x-XXXX-XXXX)
 */
function formatSafeNumber(digits: string): string {
  if (digits.length <= 4) return digits
  if (digits.length <= 7) return `${digits.slice(0, 4)}-${digits.slice(4)}`
  if (digits.length <= 11)
    return `${digits.slice(0, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`
  return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8, 12)}`
}

/**
 * 인터넷 전화 포맷 (070-XXXX-XXXX)
 */
function formatInternetPhone(digits: string): string {
  if (digits.length <= 3) return digits
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`
}

/**
 * 포맷된 전화번호에서 순수 숫자만 추출.
 * `maskPhone`/`formatPhone` 과 동일한 기준으로 digit-only 반환.
 */
export function parsePhone(value: string): string {
  return value.replace(/[^\d]/g, '')
}

/**
 * 상대 시간 포맷
 * @example formatRelativeTime(new Date('2026-03-10T09:00:00')) => '3분 전'
 */
export function formatRelativeTime(date: Date | string | number): string {
  const target = new Date(date)
  const now = Date.now()
  const diff = now - target.getTime()

  // 미래 시간
  if (diff < 0) return '방금'

  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)

  if (seconds < 60) return '방금'
  if (minutes < 60) return `${minutes}분 전`
  if (hours < 24) return `${hours}시간 전`
  if (days < 2) return '어제'
  if (days < 30) return `${days}일 전`
  if (years < 1) return `${months}개월 전`
  return `${years}년 전`
}

/**
 * 파일 사이즈 포맷
 * @example formatFileSize(1536) => '1.5KB'
 * @example formatFileSize(1048576) => '1MB'
 */
export function formatFileSize(bytes: number): string {
  if (bytes <= 0) return '0B'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const index = Math.max(
    0,
    Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)))
  )
  const size = bytes / Math.pow(1024, index)

  // 정수면 소수점 없이, 아니면 소수점 1자리
  const formatted = size % 1 === 0 ? String(size) : size.toFixed(1)

  return `${formatted}${units[index]}`
}

/**
 * 통화 포맷
 * @example formatCurrency(12000) => '₩12,000'
 * @example formatCurrency(99.99, 'USD') => '$99.99'
 */
export function formatCurrency(
  amount: number,
  currency: 'KRW' | 'USD' | 'EUR' | 'JPY' = 'KRW'
): string {
  const config: Record<string, { symbol: string; decimals: number }> = {
    KRW: { symbol: '₩', decimals: 0 },
    USD: { symbol: '$', decimals: 2 },
    EUR: { symbol: '€', decimals: 2 },
    JPY: { symbol: '¥', decimals: 0 },
  }

  const { symbol, decimals } = config[currency]
  const formatted = amount.toLocaleString('ko-KR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })

  return `${symbol}${formatted}`
}

/**
 * 카드번호 포맷
 * @example formatCardNumber('1234567890123456') => '1234 5678 9012 3456'
 */
export function formatCardNumber(value: string): string {
  const digits = value.replace(/[^\d]/g, '').slice(0, 16)
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim()
}

/**
 * 사업자등록번호 포맷
 * @example formatBusinessNumber('1234567890') => '123-45-67890'
 */
export function formatBusinessNumber(value: string): string {
  const digits = value.replace(/[^\d]/g, '').slice(0, 10)
  if (digits.length <= 3) return digits
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
}
